import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CalendarDays, MessageSquareQuote, RefreshCw, Star } from "lucide-react";
import { Brand } from "@/components/Brand";
import { api } from "@/lib/api";

function Stat({ label, value, id }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="font-display text-3xl font-extrabold text-slate-900" data-testid={`stats-total-${id}`}>{value ?? "–"}</div>
      <div className="text-xs text-slate-500 mt-1">{label}</div>
    </div>
  );
}

function Row({ w }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid={`stats-workshop-${w.workshop_id}`}>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-slate-900 flex items-center gap-2 flex-wrap">{w.title}{w.upcoming && <span className="text-[10px] font-bold uppercase bg-emerald-100 text-emerald-800 rounded-full px-2 py-0.5">Upcoming</span>}</div>
          <div className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5"><CalendarDays className="h-3.5 w-3.5" />{w.display_date}{w.time ? ` · ${w.time}` : ""} · {w.lang === "es" ? "Spanish" : "English"}</div>
        </div>
        <div className="grid grid-cols-4 gap-3 text-center">
          {[["registered", "Signed up"], ["waitlist", "Waitlist"], ["attended", "Attended"], ["answers", "Surveys"]].map(([k, l]) => (
            <div key={k}><div className="font-display text-lg font-bold text-slate-900" data-testid={`stats-${k}-${w.workshop_id}`}>{w[k]}</div><div className="text-[10px] text-slate-500">{l}</div></div>))}
        </div>
      </div>
      {w.answers > 0 && <p className="text-xs text-slate-600 mt-3 flex items-center gap-1.5"><Star className="h-3.5 w-3.5 text-amber-500 fill-amber-400" /><b>{w.helpful}</b>/5 helpful · <b>{w.recommend}</b>/5 would recommend</p>}
      {w.comments.length > 0 && (
        <div className="mt-2">
          <button onClick={() => setOpen(!open)} className="text-xs text-emerald-700 hover:underline" data-testid={`stats-comments-toggle-${w.workshop_id}`}>{open ? "Hide" : "Show"} {w.comments.length} comment{w.comments.length === 1 ? "" : "s"}</button>
          {open && <ul className="mt-2 space-y-1.5" data-testid={`stats-comments-${w.workshop_id}`}>{w.comments.map((c, i) => <li key={i} className="text-sm text-slate-600 flex gap-2"><MessageSquareQuote className="h-4 w-4 text-slate-300 shrink-0 mt-0.5" />{c}</li>)}</ul>}
        </div>
      )}
    </div>
  );
}

export default function LibraryStats() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots"; meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    const load = () => api.get(`/public/library-stats/${token}`).then((r) => setD(r.data)).catch(() => { setD(null); setMissing(true); });
    load();
    const t = setInterval(load, 60000);
    return () => { clearInterval(t); meta.remove(); };
  }, [token]);
  useEffect(() => { document.title = `${d?.library || "Library"} workshop numbers | GrantVerify`; }, [d?.library]);
  const t = d?.totals;
  return (
    <div className="min-h-screen bg-slate-50" data-testid="library-stats-page">
      <header className="bg-white border-b border-slate-200"><div className="max-w-4xl mx-auto px-4 sm:px-6 py-4"><Brand /></div></header>
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        {missing && <p className="text-slate-600" data-testid="library-stats-missing">This link is no longer active. Ask GrantVerify for a new one.</p>}
        {d && (
          <>
            <div className="eyebrow mb-2">Private workshop numbers</div>
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900" data-testid="library-stats-name">{d.library}</h1>
            <p className="mt-3 text-sm text-slate-500 flex items-start gap-1.5"><RefreshCw className="h-3.5 w-3.5 shrink-0 mt-0.5" />Updates automatically. No attendee names or contact details are shown. Please keep this link private.</p>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mt-8">
              <Stat id="workshops" label="Workshops held" value={t.workshops} />
              <Stat id="registered" label="Sign-ups" value={t.registered} />
              <Stat id="attended" label="Attended" value={t.attended} />
              <Stat id="helpful" label="Helpful (out of 5)" value={t.helpful} />
              <Stat id="recommend" label="Would recommend (out of 5)" value={t.recommend} />
            </div>
            <h2 className="mt-10 mb-3 font-display text-lg font-bold text-slate-900">Workshops</h2>
            {d.workshops.length ? <div className="space-y-3">{d.workshops.map((w) => <Row key={w.workshop_id} w={w} />)}</div> : <p className="text-slate-600">No workshops yet.</p>}
            <a href={d.page_url} className="inline-block mt-8 text-sm text-emerald-700 hover:underline" data-testid="library-stats-public-page">See your public library page</a>
          </>
        )}
      </main>
    </div>
  );
}
